import { useState, useCallback } from "react";
import type { VariableChange, MasterItemChange } from "@repo/shared";

export interface PendingVariableChange extends VariableChange {
  originalValues: {
    name: string;
    definition?: string;
    description?: string;
  };
}

export interface PendingVariableCreate {
  connectionId: string;
  appId: string;
  appName?: string;
  tempId: string;
  name: string;
  definition?: string;
  comment?: string;
}

// Create a map key for a variable change
function getChangeKey(connectionId: string, appId: string, variableId: string): string {
  return `${connectionId}:${appId}:${variableId}`;
}

function getCreateKey(connectionId: string, appId: string, tempId: string): string {
  return `${connectionId}:${appId}:create:${tempId}`;
}

export function usePendingChanges() {
  const [pendingCreates, setPendingCreates] = useState<Map<string, PendingVariableCreate>>(
    new Map(),
  );
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingVariableChange>>(
    new Map(),
  );

  const addChange = useCallback((change: PendingVariableChange) => {
    const key = getChangeKey(change.connectionId, change.appId, change.variableId);
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(key, change);
      return next;
    });
  }, []);

  const removeChange = useCallback((connectionId: string, appId: string, variableId: string) => {
    const key = getChangeKey(connectionId, appId, variableId);
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const hasChange = useCallback(
    (connectionId: string, appId: string, variableId: string): boolean => {
      const key = getChangeKey(connectionId, appId, variableId);
      return pendingChanges.has(key);
    },
    [pendingChanges],
  );

  const getChange = useCallback(
    (
      connectionId: string,
      appId: string,
      variableId: string,
    ): PendingVariableChange | undefined => {
      const key = getChangeKey(connectionId, appId, variableId);
      return pendingChanges.get(key);
    },
    [pendingChanges],
  );

  const clearAll = useCallback(() => {
    setPendingChanges(new Map());
  }, []);

  const getChangesArray = useCallback((): VariableChange[] => {
    return Array.from(pendingChanges.values()).map((change) => ({
      connectionId: change.connectionId,
      appId: change.appId,
      appName: change.appName,
      variableId: change.variableId,
      variableName: change.variableName,
      action: change.action ?? "update",
      changes: change.changes,
    }));
  }, [pendingChanges]);

  const addCreate = useCallback((create: PendingVariableCreate) => {
    const key = getCreateKey(create.connectionId, create.appId, create.tempId);
    setPendingCreates((prev) => {
      const next = new Map(prev);
      next.set(key, create);
      return next;
    });
  }, []);

  const removeCreate = useCallback((connectionId: string, appId: string, tempId: string) => {
    const key = getCreateKey(connectionId, appId, tempId);
    setPendingCreates((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const updateCreate = useCallback(
    (
      connectionId: string,
      appId: string,
      tempId: string,
      changes: { name?: string; definition?: string; comment?: string },
    ) => {
      const key = getCreateKey(connectionId, appId, tempId);
      setPendingCreates((prev) => {
        const existing = prev.get(key);
        if (!existing) return prev;

        const next = new Map(prev);
        next.set(key, {
          ...existing,
          name: changes.name ?? existing.name,
          definition: changes.definition ?? existing.definition,
          comment: changes.comment ?? existing.comment,
        });
        return next;
      });
    },
    [],
  );

  const getCreatesArray = useCallback((): VariableChange[] => {
    return Array.from(pendingCreates.values()).map((c) => ({
      connectionId: c.connectionId,
      appId: c.appId,
      appName: c.appName,
      variableId: c.tempId,
      variableName: c.name,
      action: "create",
      changes: {
        name: c.name,
        definition: c.definition,
        comment: c.comment,
      },
    }));
  }, [pendingCreates]);

  const clearAllCreates = useCallback(() => {
    setPendingCreates(new Map());
  }, []);

  const getChangesArrayWithCreates = useCallback((): VariableChange[] => {
    return [...getChangesArray(), ...getCreatesArray()];
  }, [getChangesArray, getCreatesArray]);

  const pendingCount = pendingChanges.size + pendingCreates.size;

  return {
    pendingChanges,
    pendingCreates,
    pendingCount,
    addChange,
    removeChange,
    hasChange,
    getChange,
    clearAll: () => {
      clearAll();
      clearAllCreates();
    },
    getChangesArray: getChangesArrayWithCreates,
    addCreate,
    removeCreate,
    updateCreate,
  };
}

// ----------------------------
// Master Items Pending Changes
// ----------------------------

export interface PendingMasterItemChange extends MasterItemChange {
  originalValues: {
    title: string;
    description?: string;
    // Dimension-specific
    fieldDefs?: string[];
    fieldLabels?: string[];
    labelExpression?: string;
    // Measure-specific
    expression?: string;
    label?: string;
    tags?: string[];
    numberFormat?: string;
    color?: string;
  };
}

export interface PendingMasterItemCreate {
  connectionId: string;
  appId: string;
  appName?: string;
  tempId: string;
  itemType: "dimension" | "measure";
  title: string;
  description?: string;
  // Dimension-specific
  fieldDefs?: string[];
  fieldLabels?: string[];
  grouping?: "N" | "H" | "C";
  labelExpression?: string;
  // Measure-specific
  expression?: string;
  label?: string;
  tags?: string[];
  numberFormat?: string;
  color?: string;
}

function getMasterItemChangeKey(connectionId: string, appId: string, itemId: string): string {
  return `${connectionId}:${appId}:${itemId}`;
}

function getMasterItemCreateKey(connectionId: string, appId: string, tempId: string): string {
  return `${connectionId}:${appId}:create:${tempId}`;
}

export function useMasterItemPendingChanges() {
  const [pendingCreates, setPendingCreates] = useState<Map<string, PendingMasterItemCreate>>(
    new Map(),
  );
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingMasterItemChange>>(
    new Map(),
  );

  const addChange = useCallback((change: PendingMasterItemChange) => {
    const key = getMasterItemChangeKey(change.connectionId, change.appId, change.itemId);
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(key, change);
      return next;
    });
  }, []);

  const removeChange = useCallback((connectionId: string, appId: string, itemId: string) => {
    const key = getMasterItemChangeKey(connectionId, appId, itemId);
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const hasChange = useCallback(
    (connectionId: string, appId: string, itemId: string): boolean => {
      const key = getMasterItemChangeKey(connectionId, appId, itemId);
      return pendingChanges.has(key);
    },
    [pendingChanges],
  );

  const getChange = useCallback(
    (connectionId: string, appId: string, itemId: string): PendingMasterItemChange | undefined => {
      const key = getMasterItemChangeKey(connectionId, appId, itemId);
      return pendingChanges.get(key);
    },
    [pendingChanges],
  );

  const clearAll = useCallback(() => {
    setPendingChanges(new Map());
    setPendingCreates(new Map());
  }, []);

  const getChangesArray = useCallback((): MasterItemChange[] => {
    const changes: MasterItemChange[] = Array.from(pendingChanges.values()).map((change) => ({
      connectionId: change.connectionId,
      appId: change.appId,
      appName: change.appName,
      itemId: change.itemId,
      itemType: change.itemType,
      itemTitle: change.itemTitle,
      action: change.action ?? "update",
      changes: change.changes,
    }));

    const creates: MasterItemChange[] = Array.from(pendingCreates.values()).map((c) => ({
      connectionId: c.connectionId,
      appId: c.appId,
      appName: c.appName,
      itemId: c.tempId,
      itemType: c.itemType,
      itemTitle: c.title,
      action: "create" as const,
      changes: {
        title: c.title,
        description: c.description,
        fieldDefs: c.fieldDefs,
        fieldLabels: c.fieldLabels,
        grouping: c.grouping,
        labelExpression: c.labelExpression,
        expression: c.expression,
        label: c.label,
        tags: c.tags,
        numberFormat: c.numberFormat,
        color: c.color,
      },
    }));

    return [...changes, ...creates];
  }, [pendingChanges, pendingCreates]);

  const addCreate = useCallback((create: PendingMasterItemCreate) => {
    const key = getMasterItemCreateKey(create.connectionId, create.appId, create.tempId);
    setPendingCreates((prev) => {
      const next = new Map(prev);
      next.set(key, create);
      return next;
    });
  }, []);

  const removeCreate = useCallback((connectionId: string, appId: string, tempId: string) => {
    const key = getMasterItemCreateKey(connectionId, appId, tempId);
    setPendingCreates((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const updateCreate = useCallback(
    (
      connectionId: string,
      appId: string,
      tempId: string,
      changes: Partial<
        Omit<PendingMasterItemCreate, "connectionId" | "appId" | "tempId" | "itemType">
      >,
    ) => {
      const key = getMasterItemCreateKey(connectionId, appId, tempId);
      setPendingCreates((prev) => {
        const existing = prev.get(key);
        if (!existing) return prev;

        const next = new Map(prev);
        next.set(key, { ...existing, ...changes });
        return next;
      });
    },
    [],
  );

  const pendingCount = pendingChanges.size + pendingCreates.size;

  return {
    pendingChanges,
    pendingCreates,
    pendingCount,
    addChange,
    removeChange,
    hasChange,
    getChange,
    clearAll,
    getChangesArray,
    addCreate,
    removeCreate,
    updateCreate,
  };
}

// ----------------------------
// Unified Pending Changes Hook
// ----------------------------

export function useUnifiedPendingChanges() {
  const variableChanges = usePendingChanges();
  const masterItemChanges = useMasterItemPendingChanges();

  const totalPendingCount = variableChanges.pendingCount + masterItemChanges.pendingCount;

  const clearAll = useCallback(() => {
    variableChanges.clearAll();
    masterItemChanges.clearAll();
  }, [variableChanges, masterItemChanges]);

  const getAllChangesArray = useCallback(() => {
    return {
      variableChanges: variableChanges.getChangesArray(),
      masterItemChanges: masterItemChanges.getChangesArray(),
    };
  }, [variableChanges, masterItemChanges]);

  return {
    totalPendingCount,
    clearAll,
    getAllChangesArray,
    variables: variableChanges,
    masterItems: masterItemChanges,
  };
}

export function createPendingChangesState(params: {
  variableChanges: VariableChange[];
  masterItemChanges: MasterItemChange[];
}) {
  const variableCreates = new Map<string, PendingVariableCreate>();
  const variableUpdates = new Map<string, PendingVariableChange>();

  for (const change of params.variableChanges) {
    const action = change.action ?? "update";
    if (action === "create") {
      variableCreates.set(getCreateKey(change.connectionId, change.appId, change.variableId), {
        connectionId: change.connectionId,
        appId: change.appId,
        appName: change.appName,
        tempId: change.variableId,
        name: change.changes.name ?? change.variableName,
        definition: change.changes.definition,
        comment: change.changes.comment,
      });
      continue;
    }

    variableUpdates.set(getChangeKey(change.connectionId, change.appId, change.variableId), {
      ...change,
      action,
      originalValues: {
        name: change.variableName,
        definition: undefined,
        description: undefined,
      },
    });
  }

  const masterCreates = new Map<string, PendingMasterItemCreate>();
  const masterUpdates = new Map<string, PendingMasterItemChange>();

  for (const change of params.masterItemChanges) {
    const action = change.action ?? "update";
    if (action === "create") {
      masterCreates.set(getMasterItemCreateKey(change.connectionId, change.appId, change.itemId), {
        connectionId: change.connectionId,
        appId: change.appId,
        appName: change.appName,
        tempId: change.itemId,
        itemType: change.itemType,
        title: change.changes.title ?? change.itemTitle,
        description: change.changes.description,
        fieldDefs: change.changes.fieldDefs,
        fieldLabels: change.changes.fieldLabels,
        grouping: change.changes.grouping,
        labelExpression: change.changes.labelExpression,
        expression: change.changes.expression,
        label: change.changes.label,
        tags: change.changes.tags,
        numberFormat: change.changes.numberFormat,
        color: change.changes.color,
      });
      continue;
    }

    masterUpdates.set(getMasterItemChangeKey(change.connectionId, change.appId, change.itemId), {
      ...change,
      action,
      originalValues: {
        title: change.itemTitle,
        description: undefined,
        fieldDefs: undefined,
        fieldLabels: undefined,
        labelExpression: undefined,
        expression: undefined,
        label: undefined,
        tags: undefined,
        numberFormat: undefined,
        color: undefined,
      },
    });
  }

  return {
    variables: {
      pendingChanges: variableUpdates,
      pendingCreates: variableCreates,
      pendingCount: variableUpdates.size + variableCreates.size,
    },
    masterItems: {
      pendingChanges: masterUpdates,
      pendingCreates: masterCreates,
      pendingCount: masterUpdates.size + masterCreates.size,
    },
    totalPendingCount:
      variableUpdates.size + variableCreates.size + masterUpdates.size + masterCreates.size,
  };
}
